import * as React from "react";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import theme from "../../Global/theme";
import { setCurrentStep } from "./Stepper/stepperSlice";
import { useDispatch } from "react-redux";
import NavbarStepper from "./Stepper/NavBarStepper";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useEditJobCardMutation, useDeleteJobCardMutation } from "../api/apiSlice";
import { showError } from "../../Global/notifications/notificationsSlice";
import { useConnectionMonitor } from "./connectionsTest";
import ConnectionAlert from "./ConnectionAlert";
import LoadingOverlay from "./LoadingOverlay";
import { useStepperStateRestoration } from "./useStepperStateRestoration";
import AbortJobButton from "./AbortJobButton";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const AMP_MIN = 5;
const AMP_MAX = 20;

const CHECKLIST = [
  "Verify the clamp is on the correct circuit (geyser only).",
  "Verify the clamp jaws are fully closed around a single conductor.",
  "Verify the breaker is in the ON position.",
  "Verify the meter is set to AC amps and zeroed.",
  "Re-take the reading after 10 seconds.",
];

const CLAMP_HELP = [
  "Open the clamp jaws fully and close them around a single live wire — never around a cable bundle.",
  "Position the clamp perpendicular (90°) to the wire for an accurate reading.",
  "Select AC amps (A~) on the meter and press zero/REL before clamping.",
  "Wait 5–10 seconds for the reading to stabilise before recording.",
  "Confirm you are clamping the geyser circuit only — not a shared circuit.",
];

export default function ConnectionTestInterludePage() {
  useStepperStateRestoration();

  const {
    isConnected,
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    responseTime,
    avgResponseTime,
    connectionDetails,
  } = useConnectionMonitor();

  let { serial_number } = useParams();
  let { imsi_number } = useParams();
  let { job_card_reference_id } = useParams();
  let navigate = useNavigate();
  const dispatch = useDispatch();

  const [amperes, setAmperes] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [showChecklist, setShowChecklist] = React.useState(false);
  const [abortOpen, setAbortOpen] = React.useState(false);
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [ampFocused, setAmpFocused] = React.useState(false);

  const [
    editJobCard,
    { isSuccess: isEditJobCardSuccess, isError: isEditJobCardError, isLoading: isEditJobCardLoading },
  ] = useEditJobCardMutation();

  const [deleteJobCard, { isSuccess: isDeleteSuccess }] = useDeleteJobCardMutation();

  const ampVal = parseFloat(amperes);
  const isEmpty = amperes === "";
  const isOutOfRange = !isEmpty && !isNaN(ampVal) && (ampVal < AMP_MIN || ampVal > AMP_MAX);
  const isAmpValid = !isEmpty && !isNaN(ampVal) && ampVal >= AMP_MIN && ampVal <= AMP_MAX;
  const handleChangeAmps = (event) => setAmperes(event.target.value);
  const handleStepAmps = (delta) => {
    const current = parseFloat(amperes) || 0;
    const next = Math.round((current + delta) * 10) / 10;
    setAmperes(String(next));
  };
  const handleChangeNotes = (event) => setNotes(event.target.value);

  const handleChecklistConfirm = () => setShowChecklist(false);

  const handlNext = () => {
    if (!isAmpValid) return;
    editJobCard({
      body: {
        state: "TES",
        serial_number,
        imsi_number,
        reference_id: job_card_reference_id,
        ampere: amperes,
        installer_notes: notes,
      },
    });
  };

  React.useEffect(() => {
    if (isEditJobCardSuccess) {
      navigate(`/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/connection_test/`);
    }
  }, [isEditJobCardSuccess]);

  React.useEffect(() => {
    if (isEditJobCardError) dispatch(showError("Error"));
  }, [isEditJobCardError]);

  React.useEffect(() => {
    if (isDeleteSuccess) navigate("/installer/");
  }, [isDeleteSuccess]);

  return (
    <>
      {showAlert && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="error"
        />
      )}
      {showWeakWarning && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleWeakWarningDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="warning"
          responseTime={responseTime || avgResponseTime}
        />
      )}

      <div style={{ position: "relative" }}>
        <LoadingOverlay isLoading={isEditJobCardLoading} message="Processing..." />
        <NavbarStepper />

        <Grid container alignItems="center" justifyContent="center">
          <Grid item xs={12} align="center">
            <Typography variant="h5" color="grey" sx={{ mb: 2 }}>
              Proceed with device installation.
            </Typography>
          </Grid>

          <Grid item xs={10} align="center">
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ensure the geyser is alone on circuit
            </Alert>
          </Grid>

          {/* Clamp meter help pill */}
          <Grid item xs={10} align="left" sx={{ mb: "14px" }}>
            <Box
              onClick={() => setHelpOpen(true)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                px: "12px",
                py: "6px",
                borderRadius: "20px",
                border: "1px solid rgba(253,185,64,0.35)",
                cursor: "pointer",
                "&:hover": { backgroundColor: "rgba(253,185,64,0.06)" },
              }}
            >
              <HelpOutlineIcon sx={{ color: "#FDB940", fontSize: "1rem" }} />
              <Typography sx={{ color: "#FDB940", fontSize: "0.82rem", fontWeight: 500 }}>
                Clamp meter help · how to position
              </Typography>
            </Box>
          </Grid>

          {/* Amp input */}
          <Grid item xs={10} align="center" sx={{ mt: 1.5 }}>
            <TextField
              label="Amperes (A) Measured *"
              value={amperes}
              onChange={handleChangeAmps}
              onFocus={() => setAmpFocused(true)}
              onBlur={() => setAmpFocused(false)}
              type="number"
              error={isOutOfRange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 0.1, min: 0 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      {ampFocused && (
                        <Box sx={{ display: "flex", flexDirection: "column", mr: "-2px" }}>
                          <IconButton
                            size="small"
                            onMouseDown={(e) => { e.preventDefault(); handleStepAmps(0.1); }}
                            sx={{ p: "1px", color: "rgba(255,255,255,0.6)", "&:hover": { color: "#FDB940" } }}
                          >
                            <ArrowDropUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onMouseDown={(e) => { e.preventDefault(); handleStepAmps(-0.1); }}
                            sx={{ p: "1px", color: "rgba(255,255,255,0.6)", "&:hover": { color: "#FDB940" } }}
                          >
                            <ArrowDropDownIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem", fontWeight: 500 }}>
                        A
                      </Typography>
                    </Box>
                  </InputAdornment>
                ),
              }}
              fullWidth
              variant="outlined"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
              }}
            />
            {isOutOfRange && (
              <Typography
                onClick={() => setShowChecklist(true)}
                sx={{
                  color: "#ef4444",
                  fontSize: "0.82rem",
                  mt: "4px",
                  textAlign: "left",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationColor: "rgba(239,68,68,0.4)",
                }}
              >
                Out of range — see troubleshooting checklist.
              </Typography>
            )}
          </Grid>

          <Grid item xs={10} align="center" sx={{ mt: 2, mb: 2 }}>
            <TextField
              label="Installer Notes"
              value={notes}
              onChange={handleChangeNotes}
              multiline
              rows={6}
              variant="outlined"
              fullWidth
              placeholder="Capture additional installation notes here..."
            />
          </Grid>
          <Grid item xs={12} sx={{ height: "80px" }} />
        </Grid>
      </div>

      {/* Fixed bottom bar */}
      <Box
        sx={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          display: "flex", gap: "12px",
          px: "16px", py: "14px",
          background: "rgba(13,14,20,0.95)",
          backdropFilter: "blur(12px)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <AbortJobButton />
        <Button
          disableElevation
          sx={{
            flex: 1,
            ...theme.yellowButtonStyle.root,
            border: "none",
            borderRadius: "10px",
            py: "13px",
            fontSize: "1rem",
            textTransform: "none",
            opacity: (isEditJobCardLoading || isEmpty || isOutOfRange) ? 0.5 : 1,
          }}
          onClick={handlNext}
          disabled={isEditJobCardLoading || !isConnected || isEmpty || isOutOfRange}
          startIcon={isEditJobCardLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isEditJobCardLoading ? "Processing..." : "Next →"}
        </Button>
      </Box>

      {/* Out-of-range checklist drawer */}
      <Drawer
        anchor="bottom"
        open={showChecklist}
        onClose={() => {}}
        PaperProps={{
          sx: {
            backgroundColor: "#12131A",
            borderRadius: "20px 20px 0 0",
            px: "20px",
            pt: "12px",
            pb: "32px",
          },
        }}
      >
        <Box
          sx={{
            width: "40px", height: "4px", borderRadius: "2px",
            backgroundColor: "rgba(255,255,255,0.15)",
            mx: "auto", mb: "20px",
          }}
        />
        <Typography sx={{ color: "#FDB940", fontWeight: 700, fontSize: "1.1rem", mb: "8px" }}>
          Reading out of range
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", lineHeight: 1.55, mb: "20px" }}>
          The amperes reading is outside the expected {AMP_MIN}–{AMP_MAX} A window for a single geyser element.
          Work through the checklist below before re-taking the reading.
        </Typography>

        {CHECKLIST.map((item, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: "10px", mb: "14px" }}>
            <Box sx={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
              border: "2px solid #FDB940", mt: "2px",
            }} />
            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.88rem", lineHeight: 1.5 }}>
              {item}
            </Typography>
          </Box>
        ))}

        <Button
          fullWidth
          disableElevation
          onClick={handleChecklistConfirm}
          sx={{
            mt: "8px", py: "14px", borderRadius: "12px",
            backgroundColor: "#FDB940", color: "#000",
            fontWeight: 700, fontSize: "1rem", textTransform: "none",
            "&:hover": { backgroundColor: "#e6a832" },
            "&.Mui-disabled": { backgroundColor: "rgba(253,185,64,0.3)", color: "rgba(0,0,0,0.4)" },
          }}
        >
          I've checked all of these
        </Button>

        <Typography
          onClick={() => { setShowChecklist(false); setAbortOpen(true); }}
          sx={{ textAlign: "center", mt: "16px", color: "#FDB940", fontSize: "0.88rem", cursor: "pointer" }}
        >
          Or — abort this installation
        </Typography>
      </Drawer>

      {/* Clamp meter help dialog */}
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1A1C24",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.07)",
            mx: 2,
          },
        }}
      >
        <DialogContent>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#fff", mb: "12px" }}>
            How to position the clamp meter
          </Typography>
          {CLAMP_HELP.map((tip, i) => (
            <Box key={i} sx={{ display: "flex", gap: "10px", mb: "10px" }}>
              <Typography sx={{ color: "#FDB940", fontWeight: 700, fontSize: "0.85rem", minWidth: "18px" }}>
                {i + 1}.
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                {tip}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setHelpOpen(false)}
            fullWidth
            sx={{
              backgroundColor: "rgba(255,255,255,0.06)", color: "#fff",
              borderRadius: "10px", fontWeight: 700, py: 1.2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Abort confirmation (triggered from within checklist drawer) */}
      <Dialog
        open={abortOpen}
        onClose={() => setAbortOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1A1C24",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.07)",
            mx: 2,
          },
        }}
      >
        <DialogContent>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", mb: 1 }}>
            Abort this installation?
          </Typography>
          <DialogContentText sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.55)" }}>
            This cannot be undone. The job card will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => { setAbortOpen(false); setShowChecklist(true); }}
            fullWidth
            sx={{
              backgroundColor: "rgba(255,255,255,0.06)", color: "#fff",
              borderRadius: "10px", fontWeight: 700, py: 1.2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            Back
          </Button>
          <Button
            onClick={() => deleteJobCard({ body: { reference_id: job_card_reference_id } })}
            fullWidth
            sx={{
              backgroundColor: "#ef4444", color: "#fff",
              borderRadius: "10px", fontWeight: 700, py: 1.2,
              "&:hover": { backgroundColor: "#dc2626" },
            }}
          >
            Confirm Abort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
